/**
 * SocialCapitalInfographicPage — the socialcapital.cc / individuals
 * landing poster. Faithfully mirrors the founder-approved infographic:
 *   • Left rail: What is Social Capital + How It Works (5 steps)
 *   • Center: 8-segment wheel + formula strip
 *   • Right rail: Earn / Badges / Levels / Why it matters
 *   • Bottom: Community impact counters + personal dashboard
 *
 * Public URL: /how-it-works        (canonical)
 *             /social-capital-explained   (alias)
 *
 * Note: The infographic exposes 8 public categories and 5 tier levels;
 * the DB model underneath still uses 11 buckets and 7 tiers. This is
 * intentional — the poster is a simplified public view of a richer
 * data model. Follow-up task to align once the founder confirms.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, Apple, Leaf, Tractor, BookOpen, Recycle, HandHeart, Users2,
  ShieldCheck, BarChart3, Award, Sparkles, Heart, Star, ArrowRight,
  Target, CheckCircle2, TrendingUp, Trophy, Compass, Send, Rocket,
  UserCheck, Gauge, Zap, Handshake, Feather,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";
import PublicImpactTicker from "@/components/PublicImpactTicker";

/* ------------------------------------------------------------------ */
/*  Data model — everything derived from the founder poster            */
/* ------------------------------------------------------------------ */
const WHEEL = [
  { key: "help_people",      label: "HELP PEOPLE",       icon: Users,     color: "#10B981" },
  { key: "save_food",        label: "SAVE FOOD",         icon: Apple,     color: "#84CC16" },
  { key: "protect_nature",   label: "PROTECT NATURE",    icon: Leaf,      color: "#EAB308" },
  { key: "support_farmers",  label: "SUPPORT FARMERS",   icon: Tractor,   color: "#F97316" },
  { key: "share_knowledge",  label: "SHARE KNOWLEDGE",   icon: BookOpen,  color: "#EF4444" },
  { key: "reuse_give",       label: "REUSE & GIVE",      icon: Recycle,   color: "#A855F7" },
  { key: "volunteer_serve",  label: "VOLUNTEER & SERVE", icon: HandHeart, color: "#3B82F6" },
  { key: "build_community",  label: "BUILD COMMUNITY",   icon: Users2,    color: "#14B8A6" },
];

const HOW_IT_WORKS = [
  { n: 1, title: "You Take Action",     body: "You contribute through meaningful activities across the SocialPulse network.", icon: Rocket,       color: "#22C55E" },
  { n: 2, title: "Verified & Measured", body: "Actions are verified by people, organizations or the system.",                icon: UserCheck,    color: "#3B82F6" },
  { n: 3, title: "Impact Calculated",   body: "Points are awarded based on impact, quality, verification and trust.",         icon: Gauge,        color: "#A855F7" },
  { n: 4, title: "Social Capital Earned", body: "Points are added to your account and badges are unlocked.",                  icon: Trophy,       color: "#F59E0B" },
  { n: 5, title: "Community Inspired",  body: "Your actions inspire others and create a stronger, kinder world.",             icon: Heart,        color: "#EF4444" },
];

const EARN_ACTIONS = [
  { icon: Users,      title: "Help People",      body: "Support neighbors, volunteer, mentor and contribute." },
  { icon: Apple,      title: "Save Food",        body: "Rescue, donate and reduce food waste." },
  { icon: Leaf,       title: "Protect Nature",   body: "Observe, restore and care for the environment." },
  { icon: Tractor,    title: "Support Farmers",  body: "Share knowledge, observations and practical help." },
  { icon: BookOpen,   title: "Share Knowledge",  body: "Answer questions, write articles and teach others." },
  { icon: Recycle,    title: "Reuse & Give",     body: "Give away, swap, donate and reduce waste." },
  { icon: Users2,     title: "Build Community",  body: "Organize events, projects and bring people together." },
];

const BADGES = [
  { icon: Leaf,      name: "Nature Observer",   body: "Document and protect nature.",           color: "#22C55E" },
  { icon: Apple,     name: "Food Rescuer",      body: "Rescue food, feed people.",              color: "#EF4444" },
  { icon: Users,     name: "Community Helper",  body: "Help people and strengthen communities.", color: "#F97316" },
  { icon: BookOpen,  name: "Knowledge Sharer",  body: "Share knowledge, teach and inspire.",     color: "#3B82F6" },
  { icon: Recycle,   name: "Reuse Champion",    body: "Reuse, give and reduce waste.",           color: "#10B981" },
  { icon: HandHeart, name: "Mentor",            body: "Guide, support and uplift others.",       color: "#A855F7" },
  { icon: Trophy,    name: "Impact Leader",     body: "Lead projects that create meaningful change.", color: "#F59E0B" },
];

const LEVELS = [
  { name: "New Contributor",     range: "0 – 99 Points",       color: "#94A3B8" },
  { name: "Active Contributor",  range: "100 – 499 Points",    color: "#22C55E" },
  { name: "Community Builder",   range: "500 – 1,499 Points",  color: "#3B82F6" },
  { name: "Impact Leader",       range: "1,500 – 4,999 Points", color: "#A855F7" },
  { name: "Change Maker",        range: "5,000+ Points",       color: "#F59E0B" },
];

const WHY = [
  { icon: Sparkles, title: "Builds a better you",          body: "Grow skills, confidence and purpose.",              color: "#3B82F6" },
  { icon: Users,    title: "Builds stronger communities", body: "Connect, collaborate and create change.",           color: "#10B981" },
  { icon: Leaf,     title: "Builds a better planet",       body: "Protect nature, reduce waste and care for future generations.", color: "#22C55E" },
  { icon: Heart,    title: "Builds lasting legacy",        body: "Your contributions today become inspiration for tomorrow.",    color: "#EF4444" },
];

const FORMULA = [
  { icon: Star,        label: "BASE VALUE",           color: "#22C55E" },
  { icon: ShieldCheck, label: "VERIFICATION",  sub: "(Trust)",       color: "#3B82F6" },
  { icon: Target,      label: "IMPACT",        sub: "(How Many)",    color: "#A855F7" },
  { icon: Award,       label: "QUALITY",       sub: "(How Good)",    color: "#F59E0B" },
  { icon: Heart,       label: "TRUST",         sub: "(Who You Are)", color: "#EF4444" },
  { icon: BarChart3,   label: "RARITY",        sub: "(How Unique)",  color: "#14B8A6" },
];

/* ------------------------------------------------------------------ */
/*  Wheel — swapped from hand-rolled SVG to the founder-approved       */
/*  static PNG (clear typography inside each segment, no clipped        */
/*  labels). If we ever bring back the SVG for interactivity we can    */
/*  restore it from git.                                                */
/* ------------------------------------------------------------------ */
const WHEEL_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_8926e311-9d9d-42c8-99ab-8293bca61d49/artifacts/7ujnzyd4_social-capital-diagram-new.png";

function EcosystemWheel() {
  return (
    <div className="relative w-full max-w-[560px] mx-auto" data-testid="wheel">
      <img
        src={WHEEL_IMG}
        alt="Social Capital — Build Community, Help People, Save Food, Protect Nature, Support Farmers, Share Knowledge, Reuse & Give, Volunteer & Serve. Powered by SocialPulse."
        className="w-full h-auto block"
        loading="eager"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Small building blocks                                              */
/* ------------------------------------------------------------------ */
const SectionCard = ({ children, className = "", ...rest }) => (
  <section
    className={`rounded-2xl border p-5 sm:p-6 bg-white ${className}`}
    style={{ borderColor: "rgba(0,0,0,0.08)" }}
    {...rest}
  >{children}</section>
);

const Kicker = ({ children, color = "#059669" }) => (
  <div className="text-sm font-bold uppercase tracking-[0.18em] mb-3" style={{ color }}>
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */
export default function SocialCapitalInfographicPage() {
  const { user: viewer } = useAuth() || {};
  const [impact, setImpact] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    let dead = false;
    http.get("/social-capital/impact-summary")
      .then((r) => { if (!dead) setImpact(r.data); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  useEffect(() => {
    if (!viewer?.handle && !viewer?.id) { setMe(null); return; }
    let dead = false;
    http.get(`/social-capital/portfolio/${viewer.handle || viewer.id}`)
      .then((r) => { if (!dead) setMe(r.data); })
      .catch(() => {});
    return () => { dead = true; };
  }, [viewer?.handle, viewer?.id]);

  const iam = me?.user;
  const myPts = iam?.social_capital || 0;
  const myCats = me?.category_totals || {};
  const myTopCat = Object.entries(myCats).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const maxCat = Math.max(1, ...myTopCat.map(([, v]) => v));

  return (
    <div className="max-w-[1240px] mx-auto px-4 sm:px-6 py-10"
         data-testid="socialcapital-infographic"
         style={{ background: "#FAFAF7" }}>
      <SeoHead
        section="social-capital"
        title="Social Capital — Recognize what truly matters"
        description="Social Capital is the positive value you build by helping others, sharing knowledge, protecting nature, reducing waste and strengthening communities."
        url="/how-it-works"
      />

      {/* HEADER */}
      <header className="text-center mb-8">
        <div className="text-sm font-bold uppercase tracking-[0.18em] opacity-60 mb-2">
          Powered by SocialPulse<span className="align-super">®</span>
        </div>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.02]"
            style={{ color: "#0F172A" }}
            data-testid="page-title">
          SOCIAL CAPITAL
          <span className="block text-3xl sm:text-4xl mt-2" style={{ color: "#059669" }}>
            By SocialPulse<span className="align-super text-xl">®</span>
          </span>
        </h1>
        <p className="mt-4 text-base sm:text-lg opacity-70 max-w-2xl mx-auto">
          Recognizing what truly matters — your positive contributions to
          people, communities and the planet.
        </p>
      </header>

      {/* Live network momentum — cycles the 6 impact counters. */}
      <PublicImpactTicker />

      {/* 3-COLUMN MAIN LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* LEFT RAIL */}
        <div className="lg:col-span-3 space-y-5">
          {/* WHAT IS */}
          <SectionCard className="!bg-[#F5F1FF]" data-testid="what-is">
            <Kicker color="#7C3AED">What is Social Capital?</Kicker>
            <p className="text-base leading-relaxed">
              Social Capital is the positive value you build by helping others,
              sharing knowledge, protecting nature, reducing waste and
              strengthening communities.
            </p>
            <p className="mt-3 text-base italic opacity-80">
              It&apos;s not about popularity. It&apos;s about making a real difference.
            </p>
            <div className="mt-4 space-y-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0"
                      style={{ background: "#10B981" }}><CheckCircle2 className="w-3 h-3" /></span>
                Earned by Action
              </div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0"
                      style={{ background: "#3B82F6" }}><ShieldCheck className="w-3 h-3" /></span>
                Verified by Trust
              </div>
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-white shrink-0"
                      style={{ background: "#A855F7" }}><BarChart3 className="w-3 h-3" /></span>
                Focused on Impact
              </div>
            </div>
          </SectionCard>

          {/* HOW IT WORKS */}
          <SectionCard data-testid="how-it-works">
            <Kicker>How It Works</Kicker>
            <ol className="space-y-3">
              {HOW_IT_WORKS.map((s) => (
                <li key={s.n} className="flex items-start gap-2.5" data-testid={`step-${s.n}`}>
                  <span className="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0"
                        style={{ background: s.color }}>
                    <s.icon className="w-4 h-4" />
                  </span>
                  <div>
                    <div className="text-base font-semibold leading-tight" style={{ color: s.color }}>
                      {s.n}. {s.title}
                    </div>
                    <div className="text-sm opacity-70 leading-snug mt-0.5">{s.body}</div>
                  </div>
                </li>
              ))}
            </ol>
          </SectionCard>
        </div>

        {/* CENTER — Wheel + Formula */}
        <div className="lg:col-span-6 space-y-5">
          <EcosystemWheel />

          <SectionCard className="!bg-[#FEF9F0]" data-testid="formula">
            <Kicker color="#B45309">The Social Capital Formula</Kicker>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 sm:gap-3 items-start">
              {FORMULA.map((f, i) => (
                <div key={f.label} className="flex flex-col items-center text-center">
                  <div className="w-11 h-11 rounded-full border-2 flex items-center justify-center bg-white"
                       style={{ borderColor: f.color, color: f.color }}
                       data-testid={`formula-${f.label.toLowerCase().replace(/\W+/g,'-')}`}>
                    <f.icon className="w-5 h-5" />
                  </div>
                  <div className="text-xs font-bold uppercase tracking-wider mt-2" style={{ color: f.color }}>
                    {f.label}
                  </div>
                  {f.sub && <div className="text-xs opacity-60">{f.sub}</div>}
                  {i < FORMULA.length - 1 && (
                    <div className="hidden sm:block text-xs opacity-40 mt-1">×</div>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t text-center"
                 style={{ borderColor: "rgba(0,0,0,0.06)" }}>
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-1.5 text-xs font-bold"
                   style={{ color: "#B45309" }}>
                <span className="text-lg">=</span> Social Capital Points
              </div>
            </div>
            <div className="mt-4 flex items-start gap-2 rounded-lg p-3 text-sm leading-relaxed"
                 style={{ background: "rgba(59,130,246,0.08)" }}>
              <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#3B82F6" }} />
              <span>
                Every point is recorded, transparent and auditable. Real
                actions. Real impact. Real value.
              </span>
            </div>
          </SectionCard>
        </div>

        {/* RIGHT RAIL */}
        <div className="lg:col-span-3 space-y-5">
          {/* EARN */}
          <SectionCard className="!bg-[#F0FDF4]" data-testid="earn-actions">
            <Kicker>Earn Social Capital<br /><span className="opacity-60 font-normal">Through many positive actions</span></Kicker>
            <ul className="space-y-2.5">
              {EARN_ACTIONS.map((a) => (
                <li key={a.title} className="flex items-start gap-2.5">
                  <span className="w-7 h-7 rounded-md bg-white flex items-center justify-center shrink-0"
                        style={{ color: "#065F46" }}>
                    <a.icon className="w-4 h-4" />
                  </span>
                  <div>
                    <div className="text-[15px] font-semibold leading-tight">{a.title}</div>
                    <div className="text-xs opacity-70 leading-snug">{a.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </SectionCard>

          {/* BADGES */}
          <SectionCard data-testid="badges-catalog">
            <Kicker>Badges You Can Earn</Kicker>
            <ul className="space-y-2.5">
              {BADGES.map((b) => (
                <li key={b.name} className="flex items-start gap-2.5">
                  <span className="w-8 h-8 rounded-full border-2 bg-white flex items-center justify-center shrink-0"
                        style={{ borderColor: b.color, color: b.color }}>
                    <b.icon className="w-4 h-4" />
                  </span>
                  <div>
                    <div className="text-[15px] font-semibold leading-tight">{b.name}</div>
                    <div className="text-xs opacity-70 leading-snug">{b.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </SectionCard>
        </div>
      </div>

      {/* LEVELS + WHY IT MATTERS ROW */}
      <div className="grid md:grid-cols-2 gap-5 mt-6">
        <SectionCard className="!bg-[#FEF9F0]" data-testid="levels">
          <Kicker color="#B45309">Levels of Contribution</Kicker>
          <ul className="space-y-2">
            {LEVELS.map((l) => (
              <li key={l.name} className="flex items-center gap-3">
                <span className="w-7 h-7 rounded-full flex items-center justify-center text-white shrink-0"
                      style={{ background: l.color }}>
                  <Star className="w-3.5 h-3.5" fill="#fff" />
                </span>
                <div className="flex-1 flex items-baseline justify-between gap-3">
                  <div className="text-base font-semibold" style={{ color: l.color }}>{l.name}</div>
                  <div className="text-sm opacity-70 font-mono">{l.range}</div>
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard className="!bg-[#EFF6FF]" data-testid="why-it-matters">
          <Kicker color="#1D4ED8">Why It Matters</Kicker>
          <ul className="space-y-3">
            {WHY.map((w) => (
              <li key={w.title} className="flex items-start gap-2.5">
                <span className="w-8 h-8 rounded-full flex items-center justify-center text-white shrink-0"
                      style={{ background: w.color }}>
                  <w.icon className="w-4 h-4" />
                </span>
                <div>
                  <div className="text-base font-semibold leading-tight" style={{ color: w.color }}>{w.title}</div>
                  <div className="text-sm opacity-80 leading-snug mt-0.5">{w.body}</div>
                </div>
              </li>
            ))}
          </ul>
        </SectionCard>
      </div>

      {/* YOUR IMPACT. OUR WORLD. */}
      <section
        className="mt-8 rounded-2xl border p-6 sm:p-8"
        style={{ borderColor: "rgba(0,0,0,0.08)", background: "#FEFCE8" }}
        data-testid="impact-strip"
      >
        <div className="text-center mb-5">
          <Kicker color="#B45309">Your Impact. Our World.</Kicker>
          <p className="text-sm opacity-60 -mt-1">Community totals across the SocialPulse ecosystem.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { key: "people_helped",    label: "People Helped",     icon: Users,   color: "#10B981" },
            { key: "meals_rescued",    label: "Meals Rescued",     icon: Apple,   color: "#EF4444" },
            { key: "trees_planted",    label: "Trees Planted",     icon: Leaf,    color: "#22C55E" },
            { key: "volunteer_events", label: "Volunteer Events",  icon: HandHeart, color: "#3B82F6" },
            { key: "items_reused",     label: "Items Reused",      icon: Recycle, color: "#F59E0B" },
            { key: "knowledge_shared", label: "Knowledge Shared",  icon: BookOpen, color: "#A855F7" },
          ].map((m) => {
            const v = impact ? Number(impact[m.key] || 0) : null;
            return (
              <div key={m.key} className="text-center" data-testid={`impact-${m.key}`}>
                <div className="w-10 h-10 mx-auto rounded-full flex items-center justify-center text-white"
                     style={{ background: m.color }}>
                  <m.icon className="w-5 h-5" />
                </div>
                <div className="font-display text-2xl font-black mt-2" style={{ color: m.color }}>
                  {v === null ? "…" : `${v.toLocaleString()}+`}
                </div>
                <div className="text-xs uppercase tracking-wider opacity-70 mt-0.5">{m.label}</div>
              </div>
            );
          })}
        </div>
        <div className="mt-5 text-center text-xs opacity-70">
          Together, we build a kinder, stronger and more sustainable world.
          <Heart className="w-3.5 h-3.5 inline ml-1" style={{ color: "#EF4444" }} fill="#EF4444" />
        </div>
      </section>

      {/* YOUR SOCIAL CAPITAL DASHBOARD */}
      <section
        className="mt-8 rounded-2xl border p-6 sm:p-8"
        style={{ borderColor: "rgba(0,0,0,0.08)", background: "#fff" }}
        data-testid="personal-dashboard"
      >
        <Kicker>Your Social Capital Dashboard</Kicker>
        {!iam ? (
          <div className="text-center py-8" data-testid="dashboard-signin">
            <p className="text-sm opacity-70">
              Sign in to see your Social Capital dashboard here — with your
              level, category breakdown and top badges.
            </p>
            <Link to="/login"
                  className="mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white"
                  style={{ background: "#059669" }}
                  data-testid="dashboard-signin-cta">
              Sign in <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {/* Left — identity */}
            <div className="flex items-center gap-3" data-testid="dashboard-id">
              <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold font-display text-white shrink-0"
                   style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}>
                {(iam.name || iam.handle || "?").trim().charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="font-semibold">{iam.name || iam.handle}</div>
                {iam.city && <div className="text-sm opacity-70">{[iam.city, iam.country].filter(Boolean).join(", ")}</div>}
                <div className="inline-flex items-center gap-1 mt-1 rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5 text-xs font-semibold uppercase tracking-wider">
                  <CheckCircle2 className="w-3 h-3" /> Verified Member
                </div>
              </div>
            </div>
            {/* Middle — total */}
            <div className="text-center" data-testid="dashboard-total">
              <div className="text-xs uppercase tracking-wider opacity-70">Total Social Capital</div>
              <div className="font-display text-5xl font-black mt-1"
                   style={{ color: "#059669" }}>{myPts.toLocaleString()}</div>
              <div className="text-xs opacity-70 mt-1">{iam.level}</div>
            </div>
            {/* Right — by category */}
            <div data-testid="dashboard-categories">
              <div className="text-xs uppercase tracking-wider opacity-70 mb-2">By Category</div>
              <div className="space-y-1.5">
                {myTopCat.map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="text-sm capitalize opacity-80 w-20">{k}</span>
                    <div className="flex-1 h-2 rounded-full overflow-hidden bg-slate-100">
                      <div className="h-full rounded-full"
                           style={{ width: `${(v / maxCat) * 100}%`, background: "#059669" }} />
                    </div>
                    <span className="font-mono text-xs opacity-70 w-8 text-right">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <Link to={`/passport/${iam.handle || iam.id}`}
                      className="text-xs inline-flex items-center gap-1 rounded-full border px-3 py-1 hover:bg-black/[0.02]"
                      data-testid="dashboard-view-passport">
                  Full passport <ArrowRight className="w-3 h-3" />
                </Link>
                <Link to={`/board/${iam.handle || iam.id}`}
                      className="text-xs inline-flex items-center gap-1 rounded-full border px-3 py-1 hover:bg-black/[0.02]"
                      data-testid="dashboard-view-board">
                  Printable board <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* FOOTER */}
      <footer className="mt-10 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4"
              style={{ background: "#0F172A", color: "#F8FAFC" }}>
        <div>
          <div className="font-display text-xl font-bold flex items-center gap-2">
            <span className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: "linear-gradient(135deg,#059669,#10b981)" }}>
              <Users2 className="w-4 h-4 text-white" />
            </span>
            SocialPulse
          </div>
          <div className="text-xs opacity-70 mt-1">One Person. One SocialPulse. Many Communities.</div>
        </div>
        <div className="flex flex-wrap gap-3 text-xs">
          {[["Connect", Users], ["Share", Send], ["Contribute", Heart], ["Inspire", Star], ["Grow", TrendingUp]].map(([label, Icon]) => (
            <span key={label} className="inline-flex items-center gap-1 opacity-80">
              <Icon className="w-3.5 h-3.5" /> {label}
            </span>
          ))}
        </div>
        <div className="text-sm opacity-70 max-w-[260px] leading-snug">
          Your actions create change. Your change builds the future.
          <div className="mt-1 font-mono text-emerald-400">socialcapital.cc</div>
        </div>
      </footer>
    </div>
  );
}
